import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Badge,
  Card,
  DataTable,
  EmptyState,
  PageHeader,
  SelectInput,
  useEntityList,
  type DataTableColumn,
} from "@pericles/framework-frontend";
import {
  AI_REVIEW_STATUS,
  EXECUTION_STATUS,
  PRISMA_EXECUTION_STATUS,
  type AiReviewStatus,
  type ExecutionStatus,
} from "@pericles/shared";

import {
  updateRequirementAiReviewStatus,
  updateRequirementExecutionStatus,
  useGenerateRequirementWithAI,
} from "../api/requirementsClient";
import { fetchProjectsForCustomer, type ProjectSummary } from "../api/projectsClient";
import {
  AI_REVIEW_STATUS_UI,
  EXECUTION_STATUS_UI,
  normalizeAiReviewStatus,
  normalizeExecutionStatus,
} from "../domain/statusAdapters";

import { EntityDeleteButton } from "./EntityDeleteButton";
import {
  AiReviewStatusBadge,
  RequirementReviewStatusBadge,
  renderStatusBadge,
  ExecutionStatusBadge,
  type StatusBadgeMap,
} from "./RequirementReviewStatusBadge";
import { PageShell, Section } from "./ui";

interface RequirementListItem {
  id: string | number;
  title?: string;
  customerId?: string | number;
  project?: unknown;
  aiReviewStatus?: AiReviewStatus | null;
  reviewStatus?: string;
  generationStatus?: string;
  executionStatus?: ExecutionStatus | string | null;
  generationRequested?: boolean;
  sourceType?: string | null;
}

type RequirementCoverageFilter = "all" | "unplanned" | "planned";

interface RequirementsListScreenProps<TFilter = unknown> {
  entityName: string;
  title?: string;
  filters?: TFilter;
  pageSize?: number;
  activeCustomerId?: string;
  onActiveCustomerChange?: (customerId: string) => void;
  onRowClick?: (row: RequirementListItem) => void;
  filterItems?: (row: RequirementListItem) => boolean;
  renderActions?: (row: RequirementListItem, context: { refetch: () => void }) => JSX.Element | string;
  projectIdFilter?: string;
  hideProjectFilter?: boolean;
  showDeleteAction?: boolean;
  deleteConfirmMessage?: string;
  deleteButtonLabel?: string;
  onDeleted?: (row: RequirementListItem) => void;
  enableViewTabs?: boolean;
  defaultView?: RequirementsView;
  view?: RequirementsView;
  onViewChange?: (view: RequirementsView) => void;
  renderActionsByView?: Partial<Record<RequirementsView, RequirementsListScreenProps["renderActions"]>>;
  workItemCoverageByRequirementId?: Record<string, number>;
}

export type RequirementsView = "all" | "to_review" | "to_generate";

export const GENERATION_STATUS_BADGES: StatusBadgeMap = {
  [PRISMA_EXECUTION_STATUS.notRequested]: EXECUTION_STATUS_UI[PRISMA_EXECUTION_STATUS.notRequested],
  [PRISMA_EXECUTION_STATUS.pending]: EXECUTION_STATUS_UI[PRISMA_EXECUTION_STATUS.pending],
  [PRISMA_EXECUTION_STATUS.notPlanned]: EXECUTION_STATUS_UI[PRISMA_EXECUTION_STATUS.notPlanned],
  [PRISMA_EXECUTION_STATUS.planned]: EXECUTION_STATUS_UI[PRISMA_EXECUTION_STATUS.planned],
  [PRISMA_EXECUTION_STATUS.inProgress]: EXECUTION_STATUS_UI[PRISMA_EXECUTION_STATUS.inProgress],
  [PRISMA_EXECUTION_STATUS.blocked]: EXECUTION_STATUS_UI[PRISMA_EXECUTION_STATUS.blocked],
  [PRISMA_EXECUTION_STATUS.completed]: EXECUTION_STATUS_UI[PRISMA_EXECUTION_STATUS.completed],
  [PRISMA_EXECUTION_STATUS.done]: EXECUTION_STATUS_UI[PRISMA_EXECUTION_STATUS.done],
  [PRISMA_EXECUTION_STATUS.failed]: EXECUTION_STATUS_UI[PRISMA_EXECUTION_STATUS.failed],
  [PRISMA_EXECUTION_STATUS.dropped]: EXECUTION_STATUS_UI[PRISMA_EXECUTION_STATUS.dropped],
};

const PENDING_REVIEW_VALUE = "pending_review";

const DEFAULT_AI_REVIEW_FILTERS: Array<{ value: AiReviewStatus | "all"; label: string }> = [
  { value: "all", label: "All" },
  { value: AI_REVIEW_STATUS.pending, label: AI_REVIEW_STATUS_UI[AI_REVIEW_STATUS.pending].label },
  { value: AI_REVIEW_STATUS.in_progress, label: AI_REVIEW_STATUS_UI[AI_REVIEW_STATUS.in_progress].label },
  { value: AI_REVIEW_STATUS.completed, label: AI_REVIEW_STATUS_UI[AI_REVIEW_STATUS.completed].label },
  { value: AI_REVIEW_STATUS.rejected, label: AI_REVIEW_STATUS_UI[AI_REVIEW_STATUS.rejected].label },
  { value: AI_REVIEW_STATUS.none, label: AI_REVIEW_STATUS_UI[AI_REVIEW_STATUS.none].label },
];

const REVIEW_FILTER_OPTIONS: Array<{ value: RequirementListItem["reviewStatus"] | "all"; label: string }> = [
  { value: "all", label: "All" },
  { value: "draft", label: "Draft" },
  { value: "pending_review", label: "Pending" },
  { value: "in_review", label: "In review" },
  { value: "approved", label: "Approved" },
  { value: "rejected", label: "Rejected" },
  { value: "archived", label: "Archived" },
];

const EXECUTION_STATUS_FILTERS: Array<{ value: ExecutionStatus | "all"; label: string }> = [
  { value: "all", label: "All" },
  { value: PRISMA_EXECUTION_STATUS.planned, label: EXECUTION_STATUS_UI[PRISMA_EXECUTION_STATUS.planned].label },
  { value: PRISMA_EXECUTION_STATUS.inProgress, label: EXECUTION_STATUS_UI[PRISMA_EXECUTION_STATUS.inProgress].label },
  { value: PRISMA_EXECUTION_STATUS.blocked, label: EXECUTION_STATUS_UI[PRISMA_EXECUTION_STATUS.blocked].label },
  { value: PRISMA_EXECUTION_STATUS.done, label: EXECUTION_STATUS_UI[PRISMA_EXECUTION_STATUS.done].label },
  { value: PRISMA_EXECUTION_STATUS.dropped, label: EXECUTION_STATUS_UI[PRISMA_EXECUTION_STATUS.dropped].label },
];

type RequirementProvenance = "document" | "meeting" | "ai";

const PROVENANCE_OPTIONS: Array<{ value: RequirementProvenance | "all"; label: string }> = [
  { value: "all", label: "All" },
  { value: "document", label: "Document" },
  { value: "meeting", label: "Meeting" },
  { value: "ai", label: "IA" },
];

function getProjectName(project: RequirementListItem["project"]): string {
  if (project == null) return "";
  if (typeof project === "string") return project;

  if (typeof project === "object") {
    if ("name" in project && typeof (project as any).name === "string") {
      return (project as any).name as string;
    }
    if ("title" in project && typeof (project as any).title === "string") {
      return (project as any).title as string;
    }
  }

  return String(project);
}

function compareStrings(a: string, b: string): number {
  const normalizedA = a || "";
  const normalizedB = b || "";
  return normalizedA.localeCompare(normalizedB, undefined, { sensitivity: "base" });
}

export function mapSourceTypeToProvenance(sourceType?: string | null): RequirementProvenance | null {
  if (!sourceType) return null;
  const normalized = sourceType.toLowerCase();
  if (normalized.includes("meeting")) return "meeting";
  if (normalized.includes("ai")) return "ai";
  return "document";
}

const EXECUTION_ORDER: ExecutionStatus[] = [
  PRISMA_EXECUTION_STATUS.notRequested,
  PRISMA_EXECUTION_STATUS.pending,
  PRISMA_EXECUTION_STATUS.notPlanned,
  PRISMA_EXECUTION_STATUS.planned,
  PRISMA_EXECUTION_STATUS.inProgress,
  PRISMA_EXECUTION_STATUS.blocked,
  PRISMA_EXECUTION_STATUS.completed,
  PRISMA_EXECUTION_STATUS.done,
  PRISMA_EXECUTION_STATUS.failed,
  PRISMA_EXECUTION_STATUS.dropped,
];
const REVIEW_ORDER: RequirementListItem["reviewStatus"][] = [
  "draft",
  "pending_review",
  "in_review",
  "approved",
  "rejected",
  "archived",
];

export function filterPendingReview(row: RequirementListItem): boolean {
  const normalizedStatus = row.reviewStatus?.toString().toLowerCase();
  return normalizedStatus === PENDING_REVIEW_VALUE;
}

export function filterAwaitingGeneration(row: RequirementListItem): boolean {
  const requestedGeneration = row.generationRequested === true;
  const normalizedStatus = row.generationStatus?.toString().toLowerCase();
  const alreadyGenerated = normalizedStatus === "generated";

  return requestedGeneration && !alreadyGenerated;
}

export function getExecutionStatus(row: RequirementListItem): ExecutionStatus {
  const rawStatus = row.executionStatus ?? row.generationStatus ?? PRISMA_EXECUTION_STATUS.planned;
  const normalized = normalizeExecutionStatus(rawStatus) ?? PRISMA_EXECUTION_STATUS.planned;
  return EXECUTION_ORDER.includes(normalized) ? normalized : PRISMA_EXECUTION_STATUS.planned;
}

function getRequirementCustomerId(row: RequirementListItem): string | null {
  if (row.customerId != null) {
    return String(row.customerId);
  }

  const project = row.project;
  if (project && typeof project === "object") {
    if ("customerId" in project && (project as any).customerId != null) {
      return String((project as any).customerId);
    }

    if ("customer" in project && typeof (project as any).customer === "object") {
      const customer = (project as any).customer;
      if (customer && "id" in customer && (customer as any).id != null) {
        return String((customer as any).id);
      }
    }
  }

  return null;
}

function getRequirementProjectId(row: RequirementListItem): string | null {
  const anyRow: any = row;

  if (anyRow?.projectId != null) {
    return String(anyRow.projectId);
  }

  const project = anyRow?.project;
  if (project && typeof project === "object" && "id" in project && (project as any).id != null) {
    return String((project as any).id);
  }

  return null;
}

export function RequirementsListScreen<TFilter = unknown>({
  entityName,
  title,
  filters,
  pageSize = 20,
  activeCustomerId,
  onActiveCustomerChange,
  onRowClick,
  filterItems,
  renderActions,
  projectIdFilter,
  hideProjectFilter,
  showDeleteAction = true,
  deleteConfirmMessage,
  deleteButtonLabel,
  onDeleted,
  enableViewTabs = false,
  defaultView = "all",
  view,
  onViewChange,
  renderActionsByView,
  workItemCoverageByRequirementId,
}: RequirementsListScreenProps<TFilter>): JSX.Element {
  const [page, setPage] = useState(0);
  const [projects, setProjects] = useState<ProjectSummary[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState<string | "all">(
    projectIdFilter ?? "all",
  );
  const [internalView, setInternalView] = useState<RequirementsView>(defaultView);
  const [selectedReviewStatus, setSelectedReviewStatus] = useState<RequirementListItem["reviewStatus"] | "all">("all");
  const [selectedAiReviewStatus, setSelectedAiReviewStatus] = useState<AiReviewStatus | "all">("all");
  const [selectedExecutionStatus, setSelectedExecutionStatus] = useState<ExecutionStatus | "all">("all");
  const [selectedProvenance, setSelectedProvenance] = useState<RequirementProvenance | "all">("all");
  const [selectedCoverage, setSelectedCoverage] = useState<RequirementCoverageFilter>("all");
  const [sortKey, setSortKey] = useState<"project" | "title" | "review" | "execution" | "provenance">(
    "project",
  );
  const [isLoadingProjects, setIsLoadingProjects] = useState(false);
  const [updatingAiReviewId, setUpdatingAiReviewId] = useState<string | number | null>(null);
  const [updatingExecutionId, setUpdatingExecutionId] = useState<string | number | null>(null);
  const [aiReviewError, setAiReviewError] = useState<string | null>(null);
  const [aiReviewErrorId, setAiReviewErrorId] = useState<string | number | null>(null);
  const [executionError, setExecutionError] = useState<string | null>(null);
  const [executionErrorId, setExecutionErrorId] = useState<string | number | null>(null);

  const activeView = view ?? internalView;

  useEffect(() => {
    setSelectedProjectId(projectIdFilter ?? "all");
  }, [projectIdFilter]);

  useEffect(() => {
    if (view) {
      setInternalView(view);
    }
  }, [view]);

  const mergedFilters = useMemo(() => {
    if (!activeCustomerId || activeCustomerId === "all") return filters;
    const baseFilters = (filters ?? {}) as Record<string, unknown>;
    return { ...baseFilters, customerId: activeCustomerId } as TFilter;
  }, [activeCustomerId, filters]);

  useEffect(() => {
    if (hideProjectFilter) {
      setProjects([]);
      return;
    }

    setSelectedProjectId((current) => (projectIdFilter ?? current ?? "all"));

    if (!activeCustomerId || activeCustomerId === "all") {
      setProjects([]);
      return;
    }

    let cancelled = false;
    const customerId = activeCustomerId;

    async function loadProjects() {
      setIsLoadingProjects(true);
      try {
        const list = await fetchProjectsForCustomer(customerId);
        if (!cancelled) {
          setProjects(list);
        }
      } catch (error) {
        console.error("Failed to load projects for requirements filter", error);
        if (!cancelled) {
          setProjects([]);
        }
      } finally {
        if (!cancelled) {
          setIsLoadingProjects(false);
        }
      }
    }

    void loadProjects();

    return () => {
      cancelled = true;
    };
  }, [activeCustomerId, hideProjectFilter, projectIdFilter]);

  const { data, total, isLoading, isError, error, refetch } = useEntityList<RequirementListItem, TFilter>({
    entity: entityName,
    filters: mergedFilters,
    page,
    pageSize,
  });

  useEffect(() => {
    setInternalView(defaultView);
  }, [defaultView]);

  const viewFilter = useMemo(() => {
    if (activeView === "to_review") return filterPendingReview;
    if (activeView === "to_generate") return filterAwaitingGeneration;
    return null;
  }, [activeView]);

  useEffect(() => {
    setPage(0);
  }, [activeView]);

  const filteredData = useMemo(() => {
    let items = data ?? [];
    if (filterItems) {
      items = items.filter(filterItems);
    }

    if (activeCustomerId && activeCustomerId !== "all") {
      items = items.filter((row) => getRequirementCustomerId(row) === activeCustomerId);
    }

    const effectiveProjectId = projectIdFilter ?? selectedProjectId;
    if (effectiveProjectId && effectiveProjectId !== "all") {
      items = items.filter((row) => getRequirementProjectId(row) === effectiveProjectId);
    }

    if (selectedAiReviewStatus && selectedAiReviewStatus !== "all") {
      items = items.filter((row) => {
        const normalized = normalizeAiReviewStatus(row.aiReviewStatus);
        return normalized === selectedAiReviewStatus;
      });
    }

    if (selectedReviewStatus && selectedReviewStatus !== "all") {
      items = items.filter((row) => row.reviewStatus === selectedReviewStatus);
    }

    if (selectedExecutionStatus && selectedExecutionStatus !== "all") {
      items = items.filter((row) => getExecutionStatus(row) === selectedExecutionStatus);
    }

    if (selectedProvenance && selectedProvenance !== "all") {
      items = items.filter((row) => mapSourceTypeToProvenance(row.sourceType) === selectedProvenance);
    }

    if (viewFilter) {
      items = items.filter(viewFilter);
    }

    if (workItemCoverageByRequirementId && selectedCoverage !== "all") {
      items = items.filter((row) => {
        const linkedCount = workItemCoverageByRequirementId[String(row.id)] ?? 0;
        return selectedCoverage === "planned" ? linkedCount > 0 : linkedCount === 0;
      });
    }

    return items;
  }, [
    activeCustomerId,
    data,
    filterItems,
    projectIdFilter,
    selectedAiReviewStatus,
    selectedExecutionStatus,
    selectedProvenance,
    selectedProjectId,
    selectedReviewStatus,
    selectedCoverage,
    viewFilter,
    workItemCoverageByRequirementId,
  ]);

  const sortedData = useMemo(() => {
    const items = filteredData ?? [];
    return [...items].sort((a, b) => {
      if (sortKey === "title") {
        return compareStrings(a.title ?? "", b.title ?? "");
      }

      if (sortKey === "review") {
        const aIndex = Math.max(REVIEW_ORDER.indexOf(a.reviewStatus), 0);
        const bIndex = Math.max(REVIEW_ORDER.indexOf(b.reviewStatus), 0);
        return aIndex - bIndex;
      }

      if (sortKey === "execution") {
        return EXECUTION_ORDER.indexOf(getExecutionStatus(a)) - EXECUTION_ORDER.indexOf(getExecutionStatus(b));
      }

      if (sortKey === "provenance") {
        return compareStrings(mapSourceTypeToProvenance(a.sourceType) ?? "", mapSourceTypeToProvenance(b.sourceType) ?? "");
      }

      return compareStrings(getProjectName(a.project), getProjectName(b.project)) || compareStrings(a.title ?? "", b.title ?? "");
    });
  }, [filteredData, sortKey]);

  const effectiveProjectId = projectIdFilter ?? selectedProjectId;
  const shouldUseFilteredTotal =
    Boolean(filterItems) ||
    (activeCustomerId && activeCustomerId !== "all") ||
    effectiveProjectId !== "all" ||
    selectedCoverage !== "all";
  const displayTotal = shouldUseFilteredTotal ? filteredData.length : total;

  const handleAiReviewStatusChange = useCallback(
    async (rowId: string | number, nextStatus: AiReviewStatus) => {
      setAiReviewError(null);
      setAiReviewErrorId(null);
      setUpdatingAiReviewId(rowId);

      try {
        await updateRequirementAiReviewStatus(rowId, nextStatus);
        refetch();
      } catch (err) {
        const message = err instanceof Error ? err.message : "Failed to update AI review status";
        setAiReviewError(message);
        setAiReviewErrorId(rowId);
        alert(message);
      } finally {
        setUpdatingAiReviewId(null);
      }
    },
    [refetch],
  );

  const handleExecutionStatusChange = useCallback(
    async (rowId: string | number, nextStatus: ExecutionStatus) => {
      setExecutionError(null);
      setExecutionErrorId(null);
      setUpdatingExecutionId(rowId);

      try {
        await updateRequirementExecutionStatus(rowId, nextStatus);
        refetch();
      } catch (err) {
        const message = err instanceof Error ? err.message : "Failed to update execution status";
        setExecutionError(message);
        setExecutionErrorId(rowId);
        alert(message);
      } finally {
        setUpdatingExecutionId(null);
      }
    },
    [refetch],
  );

  const resolvedRenderActions = renderActionsByView?.[activeView] ?? renderActions;

  const renderActionCell = useCallback(
    (row: RequirementListItem) => {
      const actions: JSX.Element[] = [];

      if (resolvedRenderActions) {
        const rendered = resolvedRenderActions(row, { refetch });
        actions.push(
          typeof rendered === "string" ? (
            <span key="custom" className="text-sm text-slate-700">
              {rendered}
            </span>
          ) : (
            <div key="custom">{rendered}</div>
          ),
        );
      }

      if (showDeleteAction) {
        actions.push(
          <EntityDeleteButton
            key={`delete-${row.id}`}
            entityName={entityName}
            entityId={row.id}
            confirmMessage={
              deleteConfirmMessage ?? "Delete this requirement? This action cannot be undone."
            }
            label={deleteButtonLabel}
            onDeleted={() => {
              refetch();
              onDeleted?.(row);
            }}
          />,
        );
      }

      if (actions.length === 0) return null;
      if (actions.length === 1) return actions[0];

      return <div className="flex flex-col items-end gap-1">{actions}</div>;
    },
    [
      deleteButtonLabel,
      deleteConfirmMessage,
      entityName,
      onDeleted,
      refetch,
      renderActions,
      renderActionsByView,
      resolvedRenderActions,
      showDeleteAction,
    ],
  );

  const columns = useMemo<DataTableColumn<RequirementListItem>[]>(
    () => [
      {
        id: "title",
        header: "Title",
        accessor: (row: RequirementListItem) => row.title ?? "Untitled requirement",
      },
      {
        id: "project",
        header: "Project",
        accessor: (row: RequirementListItem) => getProjectName(row.project),
      },
      {
        id: "provenance",
        header: "Provenance",
        accessor: (row: RequirementListItem) => mapSourceTypeToProvenance(row.sourceType) ?? "-",
      },
      {
        id: "aiReviewStatus",
        header: "AI Review",
        accessor: (row: RequirementListItem) => (
          <AiReviewStatusBadge
            status={normalizeAiReviewStatus(row.aiReviewStatus) ?? AI_REVIEW_STATUS.pending}
          />
        ),
      },
      {
        id: "reviewStatus",
        header: "Review Status",
        accessor: (row: RequirementListItem) => (
          <RequirementReviewStatusBadge status={row.reviewStatus} />
        ),
      },
      {
        id: "aiReviewActions",
        header: "AI Review Actions",
        accessor: (row: RequirementListItem) => (
          <div className="flex flex-col gap-1">
            <SelectInput
              className="w-48"
              value={normalizeAiReviewStatus(row.aiReviewStatus) ?? AI_REVIEW_STATUS.pending}
              onChange={(event) => {
                event.stopPropagation();
                const next = event.target.value as AiReviewStatus;
                const current = normalizeAiReviewStatus(row.aiReviewStatus);
                if (next === current) return;
                void handleAiReviewStatusChange(row.id, next);
              }}
              onClick={(event) => event.stopPropagation()}
              disabled={updatingAiReviewId === row.id}
            >
              {DEFAULT_AI_REVIEW_FILTERS.filter((option) => option.value !== "all").map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </SelectInput>
            {aiReviewError && aiReviewErrorId === row.id ? (
              <span className="text-xs text-red-600">{aiReviewError}</span>
            ) : null}
          </div>
        ),
      },
      {
        id: "executionStatus",
        header: "Execution",
        accessor: (row: RequirementListItem) => <ExecutionStatusBadge status={getExecutionStatus(row)} />,
      },
      {
        id: "executionActions",
        header: "Execution actions",
        accessor: (row: RequirementListItem) => (
          <div className="flex flex-col gap-1">
            <SelectInput
              className="w-48"
              value={getExecutionStatus(row)}
              onChange={(event) => {
                event.stopPropagation();
                const next = event.target.value as ExecutionStatus;
                if (next === getExecutionStatus(row)) return;
                void handleExecutionStatusChange(row.id, next);
              }}
              onClick={(event) => event.stopPropagation()}
              disabled={updatingExecutionId === row.id}
            >
              {EXECUTION_STATUS_FILTERS.filter(
                (option): option is { value: ExecutionStatus; label: string } => option.value !== "all",
              ).map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </SelectInput>
            {executionError && executionErrorId === row.id ? (
              <span className="text-xs text-red-600">{executionError}</span>
            ) : null}
          </div>
        ),
      },
      {
        id: "generationRequested",
        header: "Generation Requested",
        accessor: (row: RequirementListItem) => (
          <Badge
            label={row.generationRequested ? "Requested" : "Not requested"}
            variant={row.generationRequested ? "warning" : "default"}
          />
        ),
      },
      {
        id: "generationStatus",
        header: "Generation Status",
        accessor: (row: RequirementListItem) =>
          renderStatusBadge(row.generationStatus, GENERATION_STATUS_BADGES),
      },
      ...(workItemCoverageByRequirementId
        ? [
            {
              id: "workItems",
              header: "Work items",
              accessor: (row: RequirementListItem) => {
                const linkedCount = workItemCoverageByRequirementId[String(row.id)] ?? 0;
                return (
                  <Badge
                    label={linkedCount > 0 ? `Planned (${linkedCount})` : "Unplanned"}
                    variant={linkedCount > 0 ? "success" : "danger"}
                  />
                );
              },
            } as DataTableColumn<RequirementListItem>,
          ]
        : []),
      ...(resolvedRenderActions || showDeleteAction
        ? [
            {
              id: "actions",
              header: "Actions",
              accessor: renderActionCell,
            } as DataTableColumn<RequirementListItem>,
          ]
        : []),
    ],
    [
      aiReviewError,
      aiReviewErrorId,
      handleAiReviewStatusChange,
      handleExecutionStatusChange,
      renderActionCell,
      renderActions,
      renderActionsByView,
      resolvedRenderActions,
      workItemCoverageByRequirementId,
      showDeleteAction,
      updatingAiReviewId,
      updatingExecutionId,
      executionError,
      executionErrorId,
    ],
  );

  const errorNode = isError ? (
    <span className="text-sm text-red-600">
      {error instanceof Error ? error.message : "Failed to load requirements"}
    </span>
  ) : undefined;

  const resolvedTitle =
    title ??
    (activeView === "to_review"
      ? "Requirements to review"
      : activeView === "to_generate"
        ? "Requirements to generate"
        : "Requirements");

  const handleViewChange = useCallback(
    (nextView: RequirementsView) => {
      setInternalView(nextView);
      onViewChange?.(nextView);
    },
    [onViewChange],
  );

  const handleRowClick = useCallback(
    (row: RequirementListItem) => {
      const customerId = getRequirementCustomerId(row);
      if (customerId) {
        onActiveCustomerChange?.(customerId);
      }
      onRowClick?.(row);
    },
    [onActiveCustomerChange, onRowClick],
  );

  return (
    <PageShell>
      <PageHeader title={resolvedTitle} />

      {enableViewTabs ? (
        <Card bodyClassName="flex flex-wrap items-center gap-2">
          {(
            [
              { key: "all", label: "All" },
              { key: "to_review", label: "To review" },
              { key: "to_generate", label: "To generate" },
            ] satisfies { key: RequirementsView; label: string }[]
          ).map((tab) => (
            <button
              key={tab.key}
              type="button"
              className={`button ${activeView === tab.key ? "button-primary" : "button-secondary"}`}
              onClick={() => handleViewChange(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </Card>
      ) : null}

      <Section title="Filters">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-slate-600">Review</span>
            <SelectInput
              className="w-48"
              value={selectedReviewStatus}
              onChange={(event) =>
                setSelectedReviewStatus(event.target.value as RequirementListItem["reviewStatus"] | "all")
              }
            >
              {REVIEW_FILTER_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </SelectInput>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-slate-600">AI review</span>
            <SelectInput
              className="w-56"
              value={selectedAiReviewStatus}
              onChange={(event) => setSelectedAiReviewStatus(event.target.value as AiReviewStatus | "all")}
            >
              {DEFAULT_AI_REVIEW_FILTERS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </SelectInput>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-slate-600">Execution</span>
            <SelectInput
              className="w-48"
              value={selectedExecutionStatus}
              onChange={(event) => setSelectedExecutionStatus(event.target.value as ExecutionStatus | "all")}
            >
              {EXECUTION_STATUS_FILTERS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </SelectInput>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-slate-600">Provenance</span>
            <SelectInput
              className="w-40"
              value={selectedProvenance}
              onChange={(event) => setSelectedProvenance(event.target.value as RequirementProvenance | "all")}
            >
              {PROVENANCE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </SelectInput>
          </div>

          {hideProjectFilter ? null : (
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-slate-600">Project</span>
              <SelectInput
                className="w-64"
                value={selectedProjectId}
                onChange={(event) => setSelectedProjectId(event.target.value as string | "all")}
                disabled={isLoadingProjects || projects.length === 0}
              >
                <option value="all">All projects</option>
                {projects.map((project) => (
                  <option key={project.id} value={String(project.id)}>
                    {project.name ?? project.code ?? project.id}
                  </option>
                ))}
              </SelectInput>
            </div>
          )}

          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-slate-600">Sort by</span>
            <SelectInput
              className="w-40"
              value={sortKey}
              onChange={(event) => setSortKey(event.target.value as typeof sortKey)}
            >
              <option value="project">Project</option>
              <option value="title">Title</option>
              <option value="review">Review status</option>
              <option value="execution">Execution</option>
              <option value="provenance">Provenance</option>
            </SelectInput>
          </div>

          {workItemCoverageByRequirementId ? (
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-slate-600">Work items</span>
              <SelectInput
                className="w-44"
                value={selectedCoverage}
                onChange={(event) =>
                  setSelectedCoverage(event.target.value as RequirementCoverageFilter)
                }
              >
                <option value="all">All</option>
                <option value="unplanned">Unplanned only</option>
                <option value="planned">Planned only</option>
              </SelectInput>
            </div>
          ) : null}
        </div>
      </Section>

      <Card>
        <DataTable<RequirementListItem>
          columns={columns}
          data={sortedData}
          isLoading={isLoading}
          error={errorNode}
          page={page}
          pageSize={pageSize}
          totalCount={displayTotal}
          onPageChange={setPage}
          onRowClick={handleRowClick}
          rowClassName={(row) =>
            activeCustomerId && activeCustomerId !== "all" && getRequirementCustomerId(row) === activeCustomerId
              ? "data-table__row--active"
              : undefined
          }
          emptyState={<EmptyState title="No requirements" description="There are no requirements to display yet." />}
        />
      </Card>
    </PageShell>
  );
}

export function RequirementsToReviewListScreen({
  pageSize = 1000,
  ...props
}: Omit<RequirementsListScreenProps, "filters" | "filterItems">): JSX.Element {
  return (
    <RequirementsListScreen
      {...props}
      filters={undefined}
      filterItems={filterPendingReview}
      defaultView="to_review"
      pageSize={pageSize}
    />
  );
}

export function RequirementsAwaitingGenerationListScreen({
  pageSize = 1000,
  ...props
}: Omit<RequirementsListScreenProps, "filters" | "filterItems">): JSX.Element {
  const { generate, isLoading, error } = useGenerateRequirementWithAI();
  const [activeId, setActiveId] = useState<string | number | null>(null);

  const handleGenerate = useCallback(
    async (requirementId: string | number, refetch: () => void) => {
      setActiveId(requirementId);
      try {
        await generate(requirementId);
        refetch();
        alert("AI generation triggered successfully.");
      } catch (err) {
        alert(err instanceof Error ? err.message : "Failed to trigger AI generation");
      } finally {
        setActiveId(null);
      }
    },
    [generate],
  );

  return (
    <RequirementsListScreen
      {...props}
      filters={undefined}
      filterItems={filterAwaitingGeneration}
      defaultView="to_generate"
      pageSize={pageSize}
      renderActions={(row, { refetch }) => (
        <div className="flex flex-col gap-1 items-end">
          <button
            type="button"
            className="button button-primary button-sm"
            onClick={(event: any) => {
              event.stopPropagation();
              void handleGenerate(row.id, refetch);
            }}
            disabled={isLoading || activeId === row.id}
          >
            {isLoading && activeId === row.id ? "Generating…" : "Generate with AI"}
          </button>
          {error ? (
            <span className="text-xs text-red-600">
              {error instanceof Error ? error.message : "Generation failed"}
            </span>
          ) : null}
        </div>
      )}
    />
  );
}
